import {
  Record as RaRecord,
  SortPayload,
  useDataProvider,
  useListFilterContext,
} from 'react-admin';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Autocomplete } from '@material-ui/lab';

import { useLazyReferenceOptions } from 'hooks';
import {
  LAZY_AUTOCOMPLETE_STYLE,
  LazyReferenceTextField,
  OptionText,
  optionLabelOf,
  sameOptionBy,
} from 'components/LazyReferenceAutocomplete';

interface LazyReferenceInputProps {
  label: string;
  reference: string;
  source: string;
  filterToQuery?: (search: string) => Record<string, unknown> | undefined;
  sort?: SortPayload;
  perPage?: number;
  optionText?: OptionText;
  optionValue?: string;
  debounceTime?: number;
  initialValue?: RaRecord;
  'data-cy'?: string;
  // Read by react-admin's <Filter> or left over from AutocompleteInput call
  // sites; not forwarded to the Autocomplete.
  alwaysOn?: boolean;
  allowEmpty?: boolean;
  resettable?: boolean;
  suggestionLimit?: number;
}

export const LazyReferenceInput = ({
  label,
  reference,
  source,
  filterToQuery = search => (search ? { name: search } : {}),
  sort = { field: 'name', order: 'ASC' },
  perPage = 25,
  optionText = 'name',
  optionValue = 'id',
  debounceTime = 500,
  initialValue,
  'data-cy': dataCy,
}: LazyReferenceInputProps) => {
  const dataProvider = useDataProvider();
  const { setFilters, filterValues, displayedFilters } = useListFilterContext();

  const { open, setOpen, options, loading, setInputValue } =
    useLazyReferenceOptions({
      label,
      debounceTime,
      loadOptions: search =>
        dataProvider
          .getList(reference, {
            pagination: { page: 1, perPage },
            sort,
            filter: filterToQuery(search),
          })
          .then(({ data }) => data),
    });

  const labelOf = useMemo(() => optionLabelOf(optionText), [optionText]);

  const [selectedOption, setSelectedOption] = useState<RaRecord | null>(null);
  const lastFilterValue = useRef(filterValues[source]);
  const hasLoadedInitialValue = useRef(false);

  // Load initial value from filter if present (when navigating back to a list with filters)
  useEffect(() => {
    const currentFilterValue = filterValues[source];

    // If filter was cleared, reset the flag and clear selection
    if (!currentFilterValue && hasLoadedInitialValue.current) {
      hasLoadedInitialValue.current = false;
      if (selectedOption) {
        setSelectedOption(null);
        setInputValue('');
      }
      return;
    }

    // Only load initial value once when component mounts with a filter value
    if (
      currentFilterValue &&
      !hasLoadedInitialValue.current &&
      !selectedOption
    ) {
      hasLoadedInitialValue.current = true;

      let cancelled = false;

      dataProvider
        .getOne(reference, { id: currentFilterValue })
        .then(({ data }) => {
          // Only update state if the request wasn't cancelled and filter still has value
          if (!cancelled && filterValues[source] === currentFilterValue) {
            setSelectedOption(data);
            setInputValue(labelOf(data));
          }
        })
        .catch(err => {
          if (!cancelled) {
            console.error(
              '[LazyReferenceInput] Error loading initial value:',
              err,
            );
          }
        });

      return () => {
        cancelled = true;
      };
    }
  }, [
    filterValues,
    source,
    reference,
    dataProvider,
    labelOf,
    selectedOption,
    setInputValue,
  ]);

  // Sync selectedOption with initialValue prop when it changes (for backward compatibility)
  useEffect(() => {
    if (initialValue && !selectedOption) {
      setSelectedOption(initialValue);
      setInputValue(labelOf(initialValue));
    }
  }, [initialValue, selectedOption, labelOf, setInputValue]);

  // Sync with filter changes (when filter is cleared externally)
  useEffect(() => {
    const currentFilterValue = filterValues[source];

    // Only act if the filter value actually changed
    if (currentFilterValue !== lastFilterValue.current) {
      lastFilterValue.current = currentFilterValue;

      // If filter was cleared but we still have a selectedOption, clear it
      if (!currentFilterValue && selectedOption) {
        setSelectedOption(null);
        setInputValue('');
      }
    }
  }, [filterValues, source, selectedOption, setInputValue]);

  return (
    <Autocomplete
      data-cy={dataCy}
      style={LAZY_AUTOCOMPLETE_STYLE}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={labelOf}
      getOptionSelected={sameOptionBy(optionValue)}
      loading={loading}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
      }}
      value={selectedOption}
      onChange={(_, newValue) => {
        setSelectedOption(newValue);
        setInputValue(labelOf(newValue));

        // Update filters - pass undefined to clear instead of empty string
        const newFilters = { ...filterValues };
        if (newValue) {
          newFilters[source] = newValue[optionValue];
        } else {
          delete newFilters[source];
        }

        setFilters(newFilters, displayedFilters, false);
      }}
      renderInput={params => (
        <LazyReferenceTextField
          params={params}
          label={label}
          loading={loading}
        />
      )}
    />
  );
};
